import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PHONE = '34661309636';

const MESSAGES = {
  es: '¡Hola Cecilia! Me gustaría más información sobre las clases de español 🌟',
  en: 'Hi Cecilia! I would like more info about your Spanish classes 🌟',
  de: 'Hallo Cecilia! Ich hätte gerne mehr Infos zu deinen Spanischkursen 🌟',
};

const LABELS = {
  es: { open: 'Escríbeme por WhatsApp', teaser: '¿Tienes preguntas? Escríbeme :)', cta: 'Iniciar chat' },
  en: { open: 'Chat on WhatsApp', teaser: 'Any questions? Message me :)', cta: 'Start chat' },
  de: { open: 'Schreib mir auf WhatsApp', teaser: 'Fragen? Schreib mir :)', cta: 'Chat starten' },
};

export const WhatsAppButton = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [teaserShown, setTeaserShown] = useState(false);

  useEffect(() => {
    // Show teaser bubble after 8s only once per session
    if (sessionStorage.getItem('wa_teaser_shown')) return;
    const t = setTimeout(() => {
      setTeaserShown(true);
      sessionStorage.setItem('wa_teaser_shown', '1');
      // hide teaser after 6s
      setTimeout(() => setTeaserShown(false), 6000);
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  const lang = MESSAGES[language] ? language : 'es';
  const t = LABELS[lang];
  const href = `https://wa.me/${PHONE}?text=${encodeURIComponent(MESSAGES[lang])}`;

  const handleOpen = () => {
    setOpen(true);
    setTeaserShown(false);
  };

  return (
    <>
      {/* Teaser bubble */}
      <AnimatePresence>
        {teaserShown && !open && (
          <motion.button
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            onClick={handleOpen}
            data-testid="whatsapp-teaser"
            className="fixed bottom-24 right-5 z-40 bg-white text-[#4A4A4A] text-sm font-medium px-4 py-2.5 rounded-2xl shadow-xl border border-gray-100 max-w-[240px] hover:shadow-2xl transition-shadow"
          >
            <span className="block text-left">{t.teaser}</span>
            <span className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 rotate-45" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating action button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 260, damping: 20 }}
        onClick={handleOpen}
        data-testid="whatsapp-fab"
        aria-label={t.open}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BC5A] text-white shadow-2xl flex items-center justify-center transition-colors group"
      >
        <MessageCircle className="w-7 h-7 group-hover:scale-110 transition-transform" />
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
      </motion.button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
            onClick={() => setOpen(false)}
            data-testid="whatsapp-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden"
            >
              <div className="bg-[#075E54] text-white p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                    <img src="/logo.png" alt="Cecilia" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </div>
                  <div>
                    <p className="font-semibold">Cecilia Mateo</p>
                    <p className="text-xs opacity-80">Suele responder en pocas horas</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="whatsapp-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="bg-[#ECE5DD] p-5">
                <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm mb-4 relative">
                  <p className="text-sm text-[#4A4A4A]">
                    ¡Hola! ¿En qué puedo ayudarte? Escríbeme por WhatsApp y te responderé en cuanto pueda. 💬
                  </p>
                  <span className="absolute -left-1 top-3 w-2 h-2 bg-white rounded-tl-sm" />
                </div>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="whatsapp-cta-btn"
                  className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#25D366] hover:bg-[#20BC5A] text-white rounded-2xl font-bold shadow-lg transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t.cta}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
